import { MatDialog } from '@angular/material/dialog';
import { Callback } from './documents.actions';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CloseSmsConfirm } from '@modules/confirmation/store/confirmation.action';
import { DocumentsService } from '@modules/documents/services/documents.service';
import {
  DeletedDocument,
  DeleteDocument,
  DocumentsActionTypes,
  GetNeededDocuments,
  GetUploadedDocuments,
  LoadDocuments,
  LoadDocumentsSuccess,
  SetNeededDocuments,
  SetUploadedDocuments,
  SignDocument,
  SignedDocument,
  UploadDocument,
} from '@modules/documents/store/documents.actions';
import { InterceptorService } from '@modules/shared/services/interceptor.service';
import { AppState } from '@modules/shared/store';
import {
  GetConfirmCode,
  HideSpinnerLoading,
  SetSmsResponseCode,
  SetValidationsText,
} from '@modules/shared/store/common.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { forkJoin } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable()
export class DocumentsEffects {
  private actions$ = inject(Actions);
  private documentsService = inject(DocumentsService);
  private router = inject(Router);
  private interceptorService = inject(InterceptorService);
  private store = inject<Store<AppState>>(Store);
  private dialogRef = inject(MatDialog);

  public SignDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SignDocument>(DocumentsActionTypes.SignDocument),
      map(data => data.fileId),
      switchMap(fileId =>
        this.documentsService.sign(fileId).pipe(
          switchMap(res => {
            switch (res.errorInfo.error) {
              case '10103':
              case '10104':
                return [
                  new SetSmsResponseCode(res.errorInfo.error),
                  new SetValidationsText(res.errorInfo.errorText),
                ];
              case '10102':
                return [
                  new GetConfirmCode({
                    serviceToConfirm: '/file/sign',
                    resend: false,
                  }),
                  new HideSpinnerLoading(),
                ];
              default:
                this.interceptorService.unsubscribeSmsCode();
                this.store.dispatch(new CloseSmsConfirm(true));
                this.store.dispatch(new SignedDocument(fileId));
                this.dialogRef.closeAll();
                window.location.href = '/documents';
                return [];
            }
          })
        )
      )
    )
  );

  public LoadDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadDocuments>(DocumentsActionTypes.LoadDocuments),
      switchMap(() =>
        forkJoin({
          allDocuments: this.documentsService.getAllDocuments(),
          needed: this.documentsService.getNeeded(),
        })
      ),
      map(data => ({
        uploaded: data.allDocuments.files,
        needed: data.needed,
        infoBlock: data.allDocuments.infoBlock,
      })),
      switchMap(data => [new LoadDocumentsSuccess(data)])
    )
  );

  public DeleteDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteDocument>(DocumentsActionTypes.DeleteDocument),
      map(data => data.fileId),
      switchMap(fileId =>
        this.documentsService
          .delete(fileId)
          .pipe(
            switchMap(() => [new DeletedDocument(fileId), new LoadDocuments()])
          )
      )
    )
  );

  public UploadDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UploadDocument>(DocumentsActionTypes.UploadDocument),
      map(data => data.payload),
      switchMap(payload => this.documentsService.upload(payload)),
      switchMap(() => {
        this.router.navigate(['/documents']);
        return [new LoadDocuments()];
      })
    )
  );

  public GetNeeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetNeededDocuments>(DocumentsActionTypes.GetNeededDocuments),
      switchMap(() => this.documentsService.getNeeded()),
      switchMap(data => [new SetNeededDocuments(data)])
    )
  );

  public GetUploaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetUploadedDocuments>(DocumentsActionTypes.GetUploadedDocuments),
      switchMap(() => this.documentsService.getAllDocuments()),
      map(data => data.files),
      switchMap(data => [new SetUploadedDocuments(data)])
    )
  );

  public Callback$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<Callback>(DocumentsActionTypes.Callback),
        map(data => data.payload),
        switchMap(payload => this.documentsService.callback(payload))
      ),
    { dispatch: false }
  );
}
